/**
 * Departments Service
 * API calls for departments management
 */

import apiClient from '../lib/axios';
import type { Department, DepartmentDetail } from '../types';

export interface DepartmentFormData {
  name: string;
  description?: string | null;
  department_logo?: File;
  manager_ids?: number[];
  director_ids?: number[];
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  meta: PaginationMeta;
}

export interface DepartmentsQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
}

// Image cache for blob URLs
const imageCache = new Map<string, string>();

export const departmentsService = {
  /**
   * Get paginated departments with filters
   */
  getPaginated: async (params: DepartmentsQueryParams = {}): Promise<PaginatedResponse<Department>> => {
    const queryParams = new URLSearchParams();

    if (params.page) queryParams.set('page', String(params.page));
    if (params.limit) queryParams.set('limit', String(params.limit));
    if (params.search) queryParams.set('search', params.search);
    if (params.sort) queryParams.set('sort', params.sort);

    const queryString = queryParams.toString();
    const url = queryString ? `/departments?${queryString}` : '/departments';

    const { data } = await apiClient.get<PaginatedResponse<Department>>(url);
    return data;
  },

  /**
   * Get all active departments (for dropdowns)
   */
  getAll: async (): Promise<Department[]> => {
    const { data } = await apiClient.get<Department[]>('/departments?all=true');
    return data;
  },

  /**
   * Get department by ID (includes managers)
   */
  getById: async (id: number): Promise<DepartmentDetail> => {
    const { data } = await apiClient.get<DepartmentDetail>(`/department/${id}`);
    return data;
  },

  /**
   * Check if department name already exists
   */
  checkNameExists: async (name: string, excludeId?: number): Promise<{ exists: boolean }> => {
    const params = new URLSearchParams({
      action: 'check_name',
      name: name.trim(),
    });
    if (excludeId) {
      params.append('exclude_id', String(excludeId));
    }
    const { data } = await apiClient.get<{ exists: boolean }>(`/departments?${params.toString()}`);
    return data;
  },

  /**
   * Create new department
   */
  create: async (deptData: DepartmentFormData): Promise<Department> => {
    if (deptData.department_logo) {
      const formData = new FormData();
      formData.append('name', deptData.name);
      if (deptData.description) {
        formData.append('description', deptData.description);
      }
      formData.append('department_logo', deptData.department_logo);
      if (deptData.manager_ids && deptData.manager_ids.length > 0) {
        formData.append('manager_ids', JSON.stringify(deptData.manager_ids));
      }
      if (deptData.director_ids && deptData.director_ids.length > 0) {
        formData.append('director_ids', JSON.stringify(deptData.director_ids));
      }

      const { data } = await apiClient.post<Department>('/departments', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } else {
      const payload: Record<string, unknown> = {
        name: deptData.name,
        description: deptData.description || null,
      };
      if (deptData.manager_ids && deptData.manager_ids.length > 0) {
        payload.manager_ids = deptData.manager_ids;
      }
      if (deptData.director_ids && deptData.director_ids.length > 0) {
        payload.director_ids = deptData.director_ids;
      }
      const { data } = await apiClient.post<Department>('/departments', payload);
      return data;
    }
  },

  /**
   * Update department (uses POST with multipart for file uploads, PUT for JSON)
   */
  update: async (id: number, deptData: DepartmentFormData): Promise<Department> => {
    if (deptData.department_logo) {
      const formData = new FormData();
      formData.append('name', deptData.name);
      if (deptData.description !== undefined) {
        formData.append('description', deptData.description || '');
      }
      formData.append('department_logo', deptData.department_logo);
      if (deptData.manager_ids) {
        formData.append('manager_ids', JSON.stringify(deptData.manager_ids));
      }
      if (deptData.director_ids) {
        formData.append('director_ids', JSON.stringify(deptData.director_ids));
      }

      const { data } = await apiClient.post<Department>(`/department/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } else {
      const { data } = await apiClient.put<Department>(`/department/${id}`, {
        name: deptData.name,
        description: deptData.description ?? null,
        manager_ids: deptData.manager_ids ?? [],
        director_ids: deptData.director_ids ?? [],
      });
      return data;
    }
  },

  /**
   * Soft-delete department (must be empty)
   */
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/department/${id}`);
  },

  /**
   * Add manager to department
   */
  addManager: async (deptId: number, memberId: number): Promise<void> => {
    await apiClient.post(`/department/${deptId}/managers`, { member_id: memberId });
  },

  /**
   * Remove manager from department
   */
  removeManager: async (deptId: number, memberId: number): Promise<void> => {
    await apiClient.delete(`/department/${deptId}/managers/${memberId}`);
  },

  /**
   * Add director to department
   */
  addDirector: async (deptId: number, memberId: number): Promise<void> => {
    await apiClient.post(`/department/${deptId}/directors`, { member_id: memberId });
  },

  /**
   * Remove director from department
   */
  removeDirector: async (deptId: number, memberId: number): Promise<void> => {
    await apiClient.delete(`/department/${deptId}/directors/${memberId}`);
  },

  /**
   * Fetch department logo as blob URL (with JWT authentication)
   */
  fetchDepartmentImage: async (deptId: number): Promise<string | null> => {
    try {
      const cacheKey = `dept_${deptId}`;
      if (imageCache.has(cacheKey)) {
        return imageCache.get(cacheKey)!;
      }

      const response = await apiClient.get<Blob>(`/department_image/${deptId}`, {
        responseType: 'blob',
        params: { t: Date.now() },
      });

      const blobUrl = URL.createObjectURL(response.data);
      imageCache.set(cacheKey, blobUrl);
      return blobUrl;
    } catch (error) {
      console.error(`Failed to load image for department ${deptId}:`, error);
      return null;
    }
  },

  /**
   * Revoke blob URL and remove from cache
   */
  revokeDepartmentImage: (deptId: number): void => {
    const cacheKey = `dept_${deptId}`;
    const blobUrl = imageCache.get(cacheKey);
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      imageCache.delete(cacheKey);
    }
  },

  /**
   * Clear all cached department images
   */
  clearDepartmentImageCache: (): void => {
    imageCache.forEach((blobUrl) => {
      URL.revokeObjectURL(blobUrl);
    });
    imageCache.clear();
  },
};
